import { useCallback, useState } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.navCollapsed) === 'true';
  } catch {
    return false; // privacy mode or a corrupted store — expanded is the safe default
  }
}

/**
 * Whether the desktop side nav is collapsed to its icon rail, persisted across sessions.
 *
 * <p>Deliberately a plain hook with its own `useState` rather than a context provider. Every
 * authenticated screen goes through a single `DashboardLayout`, so there is exactly one consumer;
 * a provider would add a tree-wide re-render boundary to solve a sharing problem nobody has. The
 * layout does remount when a user moves between persona route trees (candidate → account, say),
 * and localStorage is what carries the preference across that remount.</p>
 *
 * <p>Expanded is the default. A first-time user should see the labels.</p>
 */
export function useNavCollapsed(): { collapsed: boolean; toggle: () => void } {
  const [collapsed, setCollapsed] = useState<boolean>(readStored);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEYS.navCollapsed, String(next));
      } catch {
        // storage unavailable — the toggle still works, it just won't outlive the session
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
